import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';
import { toValidCurrency } from '@/utility/currencies';
import { numbersLocale } from '@/utility/numbersLocale';

export enum FormatStyle {
  extended = 'extended',
  short = 'short',
}

export const formatAmount = (
  locale: Locale,
  value: number | null,
  currencyCode: string,
  formatStyle: FormatStyle = FormatStyle.extended,
  precision: number = 2,
  displayUsdcAsUsd: boolean = false,
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  try {
    const formatter = new Intl.NumberFormat(numbersLocale(locale), {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
      signDisplay: 'never',
    });

    const formatted = formatter.format(value);
    const currencySymbol = getCurrencySymbol(locale, currencyCode);
    const currencyName =
      formatStyle === FormatStyle.extended
        ? getCurrencyName(locale, currencyCode, displayUsdcAsUsd)
        : '';

    if (value < 0) {
      return ['-', currencySymbol, formatted, ' ', currencyName].join('');
    } else {
      return [currencySymbol, formatted, ' ', currencyName].join('');
    }
  } catch (error: unknown) {
    if (error instanceof RangeError) {
      console.debug(`invalid currency, apparently: "${currencyCode}"`);
      return '';
    } else {
      throw error;
    }
  }
};

export const getCurrencySymbol = (locale: Locale, currencyCode: string): string => {
  if (currencyCode === CurrencyCodeType.USDC || currencyCode === CurrencyCodeType.USDT) {
    return '$';
  } else if (currencyCode === CurrencyCodeType.UNSET) {
    return '';
  } else {
    const formatter = new Intl.NumberFormat(numbersLocale(locale), {
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
      currency: toValidCurrency(currencyCode),
    });

    const parts = formatter.formatToParts(1);
    const currencyPart = parts.find(
      (part: Intl.NumberFormatPart): boolean => part.type === 'currency',
    );

    return currencyPart?.value ?? '';
  }
};

const legacyUsdcNames: Record<string, string> = {
  en: 'Digital dollar',
  es: 'Dolar digital',
  pt: 'Dolar digital',
};

const legacyUsdtNames: Record<string, string> = {
  en: 'Tether digital dollar',
  es: 'Dolar digital Tether',
  pt: 'Dolar digital Tether',
};

export const getCurrencyName = (
  locale: Locale,
  currencyCode: string,
  displayUsdcAsUsd: boolean = false,
): string => {
  switch (currencyCode) {
    case CurrencyCodeType.USDT:
      return displayUsdcAsUsd ? (legacyUsdtNames[locale] ?? 'Tether digital dollar') : 'USDT';
    case CurrencyCodeType.USDC:
      return displayUsdcAsUsd ? (legacyUsdcNames[locale] ?? 'Digital dollar') : 'USDC';
    case CurrencyCodeType.MXNB:
      return 'MXNB';
    case CurrencyCodeType.BRL1:
      return 'BRL1';
    case CurrencyCodeType.UNSET:
      return '';
    default: {
      const formatter = new Intl.NumberFormat(numbersLocale(locale), {
        style: 'currency',
        currencyDisplay: 'name',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const parts = formatter.formatToParts(1);
      const currencyPart = parts.find(
        (part: Intl.NumberFormatPart): boolean => part.type === 'currency',
      );

      return currencyPart?.value ?? '';
    }
  }
};
